import { useState } from "react";
import {
  RotateCcw,
  Sigma,
  Hash,
  Type,
  Calendar,
  DollarSign,
  ToggleLeft,
  type LucideIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { useCommonI18n } from "@/hooks/useModuleI18n";
import type {
  AggregationDataType,
  AggregationKind,
} from "@/hooks/table/types";

interface AvailableAggregation {
  key: string;
  label: string;
  kinds: AggregationKind[];
  dataType: AggregationDataType;
}

interface AggregationsToggleProps {
  available: AvailableAggregation[];
  enabledKeys: string[];
  toggle: (key: string) => void;
  reset: () => void;
  showLabel?: boolean;
}

const dataTypeIcon: Record<AggregationDataType, LucideIcon> = {
  number: Hash,
  text: Type,
  date: Calendar,
  currency: DollarSign,
  boolean: ToggleLeft,
};

const dataTypeLabel: Record<AggregationDataType, string> = {
  number: "Número",
  text: "Texto",
  date: "Fecha",
  currency: "Moneda",
  boolean: "Booleano",
};

/**
 * Botón en el header de la tabla que abre un popover para activar/desactivar
 * agregaciones. Sigue el mismo patrón que ColumnVisibilityToggle.
 */
export function AggregationsToggle({
  available,
  enabledKeys,
  toggle,
  reset,
  showLabel = true,
}: AggregationsToggleProps) {
  const { t } = useCommonI18n();
  const [isOpen, setIsOpen] = useState(false);

  const enabledCount = enabledKeys.length;
  const totalCount = available.length;

  if (totalCount === 0) return null;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="gap-2"
          aria-label={t("table.aggregations.label")}
        >
          <Sigma className="h-4 w-4" />
          {showLabel ? (
            <span>
              {t("table.aggregations.count", {
                enabled: enabledCount,
                total: totalCount,
              })}
            </span>
          ) : (
            <span className="sr-only">
              {t("table.aggregations.count", {
                enabled: enabledCount,
                total: totalCount,
              })}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-64 p-3 z-[60]" align="end">
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-medium">{t("table.aggregations.title")}</h4>
            <Button
              variant="ghost"
              size="sm"
              onClick={reset}
              className="h-8 px-2"
              disabled={enabledCount === 0}
              aria-label={t("table.aggregations.reset")}
            >
              <RotateCcw className="h-3 w-3" />
            </Button>
          </div>

          <Separator />

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {available.map((agg) => {
              const isEnabled = enabledKeys.includes(agg.key);
              const Icon = dataTypeIcon[agg.dataType];
              return (
                <div
                  key={agg.key}
                  className="flex items-center space-x-3 py-1"
                >
                  <Checkbox
                    id={`agg-${agg.key}`}
                    checked={isEnabled}
                    onCheckedChange={() => toggle(agg.key)}
                    className="h-4 w-4"
                  />
                  <Icon
                    className="h-4 w-4 text-muted-foreground shrink-0"
                    aria-label={dataTypeLabel[agg.dataType]}
                  />
                  <Label
                    htmlFor={`agg-${agg.key}`}
                    className="text-sm truncate cursor-pointer flex-1"
                  >
                    {agg.label}
                  </Label>
                </div>
              );
            })}
          </div>

          {enabledCount === 0 && (
            <div className="text-xs text-muted-foreground">
              {t("table.aggregations.empty")}
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export default AggregationsToggle;